/**
 * Pure hide-condition evaluation for the system-module configurator.
 *
 * Extracted from `SystemModuleConfigurator.tsx` so the logic can be unit-tested
 * in isolation (no React/Chakra/formik imports). The component imports these
 * back — behaviour is unchanged apart from the STO-14 `disableIfHidden` fix in
 * `isFieldHidden`.
 */

export type HideConditionShape = {
  targetFieldId: string
  operator: string
  targetFieldValue: string
  disableIfHidden?: boolean
  condition?: string
}

/** Minimal structural shape needed to evaluate hide conditions. */
export type HideEvalField = {
  id: string
  valueType?: string
  hideConditions?: HideConditionShape[] | null
}

export const operatorFunctions = {
  '=': function (a: string | number, b: string | number) {
    return a === b
  },
  '>': function (a: string | number, b: string | number) {
    return a > b
  },
  '>=': function (a: string | number, b: string | number) {
    return a >= b
  },
  '<=': function (a: string | number, b: string | number) {
    return a <= b
  },
  '<': function (a: string | number, b: string | number) {
    return a < b
  },
  '!=': function (a: string | number, b: string | number) {
    return a !== b
  },
}

export function parseValueBasedOnStringPure(input: string): number | string {
  const n = parseFloat(input)
  if (!isNaN(n)) return n
  return input
}

/**
 * Recursive hide evaluation off form values — no dependency on async state.
 * Returns true when a field is hidden. OR-evaluates against the visible targets.
 *
 * When a hide condition's target field is itself hidden the condition can't be
 * evaluated. It then contributes to a cascade-hide ONLY when the condition opts
 * in via `disableIfHidden` ("Deaktivieren wenn versteckt"); otherwise it is
 * ignored, as long as at least one other condition IS evaluable (STO-14).
 *
 * When NO condition is evaluable at all — the whole dependency chain is unresolved — the
 * conditions are evaluated against empty target values instead of defaulting to visible
 * (see `triggersWithoutValues`).
 *
 * Conditions whose target field doesn't exist in `allFields` are skipped
 * entirely (treated as if they weren't declared — matches legacy behaviour).
 */
export function isFieldHidden(
  field: HideEvalField | undefined,
  formValues: Record<string, unknown>,
  allFields: Array<HideEvalField | undefined>,
  visiting: Set<string> = new Set(),
): boolean {
  if (!field?.hideConditions || field.hideConditions.length === 0) return false
  if (visiting.has(field.id)) return false
  const next = new Set(visiting)
  next.add(field.id)

  let realConditionCount = 0
  let anyVisible = false
  let anyTriggers = false
  for (const c of field.hideConditions) {
    const target = allFields.find((af) => af?.id === c.targetFieldId)
    if (!target) {
      // Dangling target — preserve legacy "unchecked checkbox" shortcut and otherwise ignore.
      if (c.targetFieldValue === 'false' && !Object.keys(formValues).includes(c.targetFieldId)) {
        realConditionCount++
        anyVisible = true
        anyTriggers = true
      }
      continue
    }
    if (isFieldHidden(target, formValues, allFields, next)) {
      // Target itself hidden: contribute to cascade-hide only when this condition
      // opts in via disableIfHidden ("Deaktivieren wenn versteckt"). Otherwise
      // ignore it so the field stays visible instead of vanishing (STO-14).
      if (c.disableIfHidden) realConditionCount++
      continue
    }
    realConditionCount++
    anyVisible = true

    const raw = formValues[target.id]
    const value = target.valueType === 'BOOL'
      ? (raw !== undefined && raw !== '' ? String(raw) : 'false')
      : (raw !== undefined ? String(raw ?? '') : '')
    const triggers = operatorFunctions[c.operator as keyof typeof operatorFunctions]?.(
      parseValueBasedOnStringPure(value),
      parseValueBasedOnStringPure(c.targetFieldValue),
    ) ?? false
    if (triggers) anyTriggers = true
  }

  if (!anyVisible) {
    // All opted-in (disableIfHidden) conditions reference hidden targets → cascade hide.
    if (realConditionCount > 0) return true
    // Nothing was evaluable: every target is itself hidden, so the whole dependency chain is
    // unresolved (KMMI: `Durchmesser` depends on `Systemauswahl`, which depends on `Druck` — as
    // long as Druck is unanswered both Systemauswahl variants are hidden). Instead of defaulting
    // to "visible", evaluate the conditions against the targets' empty values: a `!=` condition
    // ("show only for X") then hides the field, while a `=` condition ("hide when X") keeps it
    // visible — which is what STO-14 requires.
    return triggersWithoutValues(field, allFields)
  }
  return anyTriggers
}

/**
 * OR-evaluates a field's hide conditions as if every target were unanswered. Used only when no
 * condition could be evaluated at all (see `isFieldHidden`).
 */
function triggersWithoutValues(field: HideEvalField, allFields: Array<HideEvalField | undefined>): boolean {
  return field.hideConditions?.some((c) => {
    const target = allFields.find((af) => af?.id === c.targetFieldId)
    if (!target) return false
    const empty = target.valueType === 'BOOL' ? 'false' : ''
    return operatorFunctions[c.operator as keyof typeof operatorFunctions]?.(
      parseValueBasedOnStringPure(empty),
      parseValueBasedOnStringPure(c.targetFieldValue),
    ) ?? false
  }) ?? false
}

/** Ids of the fields that `formValues` hide, evaluated with {@link isFieldHidden}. */
export function hiddenFieldIdSet(
  allFields: Array<HideEvalField | undefined>,
  formValues: Record<string, unknown>,
): Set<string> {
  const hidden = new Set<string>()
  for (const field of allFields) {
    if (field && isFieldHidden(field, formValues, allFields)) hidden.add(field.id)
  }
  return hidden
}
